/**
 * Shared contracts for the embedded Spatial Analysis workflow.
 *
 * These types are the single source of truth for the frontend and mirror the
 * backend Pydantic models in `backend/models/spatial_analysis.py` field-for-field
 * (camelCase on both sides), so run state flows directly over JSON without mapping.
 *
 * The MVP transport is polling (`GET /runs/{id}` returns the full run); the event
 * union below is retained in the contract so an SSE transport can be added later
 * without changing consumers.
 */

export type SpatialAnalysisRunStatus =
  | 'queued'
  | 'running'
  | 'drafting'
  | 'complete'
  | 'failed'
  | 'cancelled';

export type SpatialAnalysisStepStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'skipped'
  | 'failed';

export interface SpatialAnalysisStep {
  id: string;
  label: string;
  status: SpatialAnalysisStepStatus;
  summary?: string;
  /** True when the step fell back to fixture/estimate because a real source was unavailable. */
  degraded?: boolean;
  startedAt?: string;
  completedAt?: string;
}

export interface SpatialAnalysisSource {
  polygonId: string;
  polygonName?: string;
  layerId?: number;
  layerName?: string;
  mapUrl?: string;
}

export interface SpatialAnalysisMetrics {
  studyArea?: {
    areaKm2?: number;
    centroid?: { lat: number; lng: number };
    vertexCount?: number;
  };
  population?: {
    worldPopEstimate?: number;
    worldPopYear?: number;
    censusGeography?: string;
    censusPopulation?: number;
    trendSummary?: string;
    /** Census provenance, e.g. 'ABS 2021', 'US Census ACS 5-yr (2022)', 'ONS Census 2021', "Scotland's Census 2022 (NRS)". */
    source?: string;
    /** Median age in years (AU/US/England & Wales/Scotland; other regions omit). */
    medianAgeYears?: number;
    /** Age-band population shares summing ~1, e.g. { '0-14': 0.18, '15-64': 0.65, '65+': 0.17 }. */
    ageGroupDistribution?: Record<string, number>;
    /** Journey-to-work mode shares, e.g. { car: 0.62, transit: 0.18, walk: 0.12, bike: 0.05, home: 0.03 }. */
    transportModeShare?: Record<string, number>;
    /** Share of the population reporting a disability (0..1). */
    disabilityPrevalence?: number;
    medianHouseholdIncome?: number;
    /** 'USD' | 'GBP' | 'AUD'. */
    incomeCurrency?: string;
    /** Reporting period for medianHouseholdIncome: 'weekly' | 'monthly' | 'annual'. */
    incomePeriod?: string;
  };
  transit?: {
    stopCount?: number;
    routeCount?: number;
    modes?: string[];
    headwayBands?: Record<string, number>;
    /** True when a clipped GTFS feed was loaded into the Fixed Routes drawer; false = OSM-only. */
    gtfsFeedAvailable?: boolean;
    /** Provenance of the transit data: 'bods' (UK Bus Open Data Service), 'transitland', or 'osm'. */
    source?: string;
    /** Attribution to display for the transit source (e.g. the BODS OGL v3.0 line). */
    attribution?: string;
  };
  gaps?: {
    uncoveredAreaKm2?: number;
    uncoveredPopulationEstimate?: number;
    uncoveredFragmentCount?: number;
  };
  /**
   * Headway-conditioned transit deserts: population beyond a walk buffer of stops
   * meeting a service-frequency cutoff. `uncoveredByBand` keys are
   * `{distance}m_le{cutoff}min` (e.g. '400m_le30min'); the `primary*` fields describe
   * the headline definition (default 400 m / ≤30 min) shown on the map + metrics.
   */
  transitDeserts?: {
    uncoveredByBand?: Record<string, number | null>;
    primaryUncoveredPopulation?: number;
    primaryUncoveredAreaKm2?: number;
    primaryDistanceM?: number;
    primaryHeadwayMin?: number;
    fragmentCount?: number;
  };
  drt?: {
    candidateCount?: number;
    reviewOnlyCount?: number;
    estimatedDailyTrips?: number;
    estimatedVehiclesNeeded?: number;
    recommendedCandidateIds?: string[];
  };
}

export type SpatialAnalysisLayerRole =
  | 'study_area'
  | 'transit_supply'
  | 'amenity_anchor'
  | 'access_gap'
  | 'transit_desert'
  | 'drt_candidate'
  | 'drt_rejected'
  | 'coverage';

export interface SpatialAnalysisLayerStyle {
  color: string;
  fillColor?: string;
  fillOpacity?: number;
  weight?: number;
  icon?: string;
}

export interface SpatialAnalysisLayer {
  id: string;
  name: string;
  role: SpatialAnalysisLayerRole;
  visibleByDefault: boolean;
  style: SpatialAnalysisLayerStyle;
  geojson: GeoJSON.FeatureCollection;
  summary?: Record<string, unknown>;
}

export interface SpatialAnalysisEvidenceNote {
  id: string;
  text: string;
  stepId?: string;
  /** Layer id this note refers to, so the UI can focus/toggle the geometry. */
  layerId?: string;
  createdAt?: string;
}

export interface SpatialAnalysisReportSection {
  id: string;
  heading: string;
  body: string;
  /** Optional layer/feature references this section is grounded in. */
  layerRefs?: string[];
  order?: number;
}

export interface SpatialAnalysisReportDraft {
  sections: SpatialAnalysisReportSection[];
  generatedAt?: string;
}

export interface SpatialAnalysisArtifact {
  name: string;
  contentType: string;
  /** Relative artifact endpoint or URL; optional in MVP. */
  url?: string;
}

export type SpatialAnalysisPublishStatus = 'unavailable' | 'available' | 'published';

export interface SpatialAnalysisPublishState {
  status: SpatialAnalysisPublishStatus;
  jiraIssueKey?: string;
  confluenceUrl?: string;
  message?: string;
}

export interface SpatialAnalysisRun {
  id: string;
  status: SpatialAnalysisRunStatus;
  createdAt: string;
  updatedAt: string;
  source: SpatialAnalysisSource;
  progress: SpatialAnalysisStep[];
  metrics: SpatialAnalysisMetrics;
  layers: SpatialAnalysisLayer[];
  notes: SpatialAnalysisEvidenceNote[];
  report?: SpatialAnalysisReportDraft;
  artifacts: SpatialAnalysisArtifact[];
  publish?: SpatialAnalysisPublishState;
  /** True when any step degraded (used a fixture/estimate rather than a real source). */
  degraded?: boolean;
  error?: string;
}

/**
 * Event union — retained for a future SSE transport. The MVP polls full run
 * state, but the fixture worker records these events and the reducer/consumers
 * can be driven by them when streaming is added.
 */
export type SpatialAnalysisEvent =
  | { type: 'run_started'; run: SpatialAnalysisRun }
  | { type: 'step_started'; stepId: string; label: string }
  | { type: 'step_completed'; stepId: string; summary?: string }
  | { type: 'step_failed'; stepId: string; error: string }
  | { type: 'metrics_updated'; metrics: Partial<SpatialAnalysisMetrics> }
  | { type: 'layers_added'; layers: SpatialAnalysisLayer[] }
  | { type: 'evidence_note_added'; note: SpatialAnalysisEvidenceNote }
  | { type: 'report_section_updated'; section: SpatialAnalysisReportSection }
  | { type: 'artifact_ready'; artifact: SpatialAnalysisArtifact }
  | { type: 'run_completed'; run: SpatialAnalysisRun }
  | { type: 'run_failed'; error: string };

/** Request body for `POST /api/spatial-analysis/runs`. */
export interface CreateSpatialAnalysisRunRequest {
  source: SpatialAnalysisSource;
  /** Study-area polygon as `[lat, lng]` rings (Atlas convention). */
  polygonCoordinates: [number, number][][];
}

export const SPATIAL_ANALYSIS_TERMINAL_STATUSES: ReadonlySet<SpatialAnalysisRunStatus> = new Set<
  SpatialAnalysisRunStatus
>(['complete', 'failed', 'cancelled']);

export function isTerminalStatus(status: SpatialAnalysisRunStatus): boolean {
  return SPATIAL_ANALYSIS_TERMINAL_STATUSES.has(status);
}
